import { Box, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { GameContext } from '../../../contexts/GameContext';
import { AlertContext } from '../../../contexts/AlertContext';
import { TurnStateEnum } from '../../../Enums/TurnStateEnum';
import SocketContext from '../../../contexts/SocketContext';

export interface BetSlotProps {
    id: string;
}

const BetSlot = (props: BetSlotProps) => {
    const [isSelectedByTurnHolder, setIsSelectedByTurnHolder] = useState(false);
    const [isSelectedByPlayer, setIsSelectedByPlayer] = useState(false);
    const [isSelectedByMe, setIsSelectedByMe] = useState(false);
    const [isClickAvailable, setIsClickAvailable] = useState(false);
    const [shouldTransform, setShouldTransform] = useState(false);
    const [shouldTransformHover, setShouldTransformHover] = useState(false);
    const [betterNames, setBetterNames] = useState<{ name: string; color: string }[]>([]);
    const gameContext = useContext(GameContext);
    const alertContext = useContext(AlertContext);
    const { socket } = useContext(SocketContext).SocketState;

    const updateTurnHolderBet = async () => {
        const result = await gameContext.getClient().UpdateTurnHolderBet(props.id);

        if (!result.success) {
            alertContext?.setAlertMessage(result.errorMessage);
            return;
        }
    };

    const removeTurnHolderBet = async () => {
        const result = await gameContext.getClient().UpdateTurnHolderBet('');

        if (!result.success) {
            alertContext?.setAlertMessage(result.errorMessage);
            return;
        }
    };

    const updateOtherPlayersBets = async () => {
        const result = await gameContext.getClient().UpdateOtherPlayersBets(props.id);

        if (!result.success) {
            alertContext?.setAlertMessage(result.errorMessage);
            return;
        }
    };

    const removePlayerBet = async () => {
        const result = await gameContext.getClient().RemovePlayerBet();

        if (!result.success) {
            alertContext?.setAlertMessage(result.errorMessage);
            return;
        }
    };

    const handleClick = () => {
        if (isClickAvailable && gameContext.currentTurn?.turnState === TurnStateEnum.waitingTurnHolderValidation) {
            if (isSelectedByTurnHolder) {
                removeTurnHolderBet();
            } else {
                updateTurnHolderBet();
            }
        } else if (isClickAvailable && gameContext.currentTurn?.turnState === TurnStateEnum.waitingEndOfTurnValidation) {
            if (isSelectedByMe) {
                removePlayerBet();
            } else {
                updateOtherPlayersBets();
            }
        }
    };

    useEffect(() => {
        const currentTurn = gameContext.currentTurn;
        const isPlayerTurn = gameContext.isPlayerTurn();

        if (!currentTurn) {
            return;
        }

        const setShouldTransformValue = () => {
            if (currentTurn.turnState === TurnStateEnum.turnFinished) {
                setShouldTransform(false);
                return;
            }

            if (isSelectedByTurnHolder || isSelectedByPlayer) {
                setShouldTransform(true);
            }

            if (!isSelectedByTurnHolder && !isSelectedByPlayer) {
                setShouldTransform(false);
            }
        };

        const setShouldTransformHoverValue = () => {
            if (currentTurn.turnState === TurnStateEnum.turnFinished) {
                setShouldTransformHover(false);
                return;
            }

            if (isSelectedByTurnHolder || isSelectedByPlayer) {
                setShouldTransformHover(true);
            } else {
                setShouldTransformHover(isClickAvailable);
            }
        };

        const setIsSelected = () => {
            currentTurn.turnHolderBet === props.id ? setIsSelectedByTurnHolder(true) : setIsSelectedByTurnHolder(false);

            if (Object.keys(currentTurn.playersBets).length === 0) {
                setIsSelectedByPlayer(false);
                setIsSelectedByMe(false);
                setBetterNames([]);
            }

            let foundPlayer = false;
            let foundMe = false;
            const names: { name: string; color: string }[] = [];
            for (const [playerId, bet] of Object.entries(currentTurn.playersBets)) {
                if (bet === props.id) {
                    foundPlayer = true;
                    if (playerId === socket?.id) {
                        foundMe = true;
                    }
                    const player = gameContext.players.find((p) => p.playerId === playerId);
                    if (player) {
                        names.push({ name: player.playerName, color: player.playerColor });
                    }
                }
            }

            if (currentTurn.turnHolderBet === props.id) {
                names.unshift({ name: currentTurn.turnHolder.playerName, color: currentTurn.turnHolder.playerColor });
            }

            setBetterNames(names);
            setIsSelectedByPlayer(foundPlayer);
            setIsSelectedByMe(foundMe);
        };

        const getIsClickAvailable = () => {
            if (currentTurn.turnState === TurnStateEnum.waitingTurnHolderValidation) {
                if (isSelectedByTurnHolder && isPlayerTurn) {
                    setIsClickAvailable(true);
                } else if (!isSelectedByPlayer && !isSelectedByTurnHolder) {
                    setIsClickAvailable(isPlayerTurn);
                } else {
                    setIsClickAvailable(false);
                }
            } else if (currentTurn.turnState === TurnStateEnum.waitingEndOfTurnValidation) {
                if (isSelectedByMe) {
                    setIsClickAvailable(true);
                } else if (!isSelectedByPlayer && !isSelectedByTurnHolder) {
                    const myPlayer = gameContext.players.find((p) => p.playerId === socket?.id);
                    setIsClickAvailable(!isPlayerTurn && (myPlayer?.tokenCount ?? 0) > 0);
                } else {
                    setIsClickAvailable(false);
                }
            } else if (currentTurn.turnState === TurnStateEnum.turnFinished) {
                setIsClickAvailable(false);
            }

            return false;
        };

        getIsClickAvailable();
        setIsSelected();
        setShouldTransformValue();
        setShouldTransformHoverValue();
    }, [gameContext, gameContext.currentTurn, isClickAvailable, isSelectedByPlayer, isSelectedByTurnHolder, isSelectedByMe, props.id]);

    const isBetActive = isSelectedByTurnHolder || isSelectedByPlayer;
    const turnHolderColor = gameContext.currentTurn?.turnHolder.playerColor ?? '#fff';
    const firstBetColor = betterNames[0]?.color ?? '#fff';
    const strokeColor = isBetActive ? firstBetColor : 'rgba(255, 255, 255, 0.35)';
    const glowColor = isBetActive ? firstBetColor : 'rgba(255, 255, 255, 0.35)';

    const crossPath = 'M12 2 L12 22 M2 12 L22 12';

    return (
        <Box
            onClick={handleClick}
            sx={{
                cursor: !isClickAvailable ? 'default' : 'pointer',
                position: 'relative',
                marginLeft: { xs: '4px', sm: '15px' },
                marginRight: { xs: '4px', sm: '15px' },
                paddingRight: { xs: '8px', sm: '30px' },
                paddingLeft: { xs: '8px', sm: '30px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transform: shouldTransform ? 'scale(3.5)' : 'none',
                transition: 'transform 0.15s ease',
                '&:hover': {
                    transform: shouldTransformHover ? 'scale(3.5)' : 'none'
                }
            }}
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {isBetActive && (
                    <defs>
                        <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="1.2" result="blur" />
                        </filter>
                    </defs>
                )}
                {isBetActive && (
                    <>
                        <path d={crossPath} stroke={glowColor} strokeWidth="0.8" strokeLinecap="round" fill="none" filter="url(#neonGlow)" />
                        <circle cx="12" cy="12" r="2" stroke={glowColor} strokeWidth="0.6" fill="none" filter="url(#neonGlow)" />
                    </>
                )}
                <path d={crossPath} stroke={strokeColor} strokeWidth="0.8" strokeLinecap="round" fill="none" />
                <circle cx="12" cy="12" r="2" stroke={strokeColor} strokeWidth="0.6" fill="none" />
            </svg>
            {shouldTransform && betterNames.length > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5px',
                        marginTop: '1px'
                    }}
                >
                    {betterNames.map((better, i) => (
                        <Typography
                            key={i}
                            sx={{
                                fontSize: '3px',
                                lineHeight: 1,
                                color: better.color,
                                textAlign: 'center',
                                textShadow: 'none',
                                whiteSpace: 'nowrap',
                                letterSpacing: '0.3px'
                            }}
                        >
                            {better.name}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default BetSlot;
